/** IANA timezone options for the Combobox picker.
 *
 *  value === label === the IANA id we store (e.g. 'America/Sao_Paulo'), so the picker
 *  round-trips exactly and the API's @IsIn(IANA_ZONES) guard accepts it. The hint shows
 *  the CURRENT UTC offset (approximate — it shifts with DST; it's an orientation aid, not
 *  the stored value). Built from Intl.supportedValuesOf — no dependency, evergreen browsers
 *  + Node 22 both have it. `hint` is a memoized getter so the ~418 offset computations only
 *  run for options a picker actually reads — importing browserTimezone (e.g. the hot auth
 *  page) pays nothing for the offsets. */
export interface TzOption {
  value: string;
  label: string;
  hint: string;
  icon: string;
}

/** e.g. 'GMT-3' / 'GMT+5:30' for the given zone right now. */
export function tzOffsetLabel(tz: string): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return name || 'GMT';
  } catch {
    return 'GMT';
  }
}

export const TIMEZONE_OPTIONS: TzOption[] = (() => {
  let zones: string[];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = ['America/Sao_Paulo'];
  }
  // hint is lazy + memoized: 418 shortOffset formatters are only built when the picker
  // renders an option, not eagerly for every module importer (auth page imports this file).
  return zones.map((z) => {
    let hint: string | undefined;
    return {
      value: z,
      label: z,
      icon: 'global-02',
      get hint() {
        return (hint ??= tzOffsetLabel(z));
      },
    };
  });
})();

/** The browser's own zone, for pre-filling a picker before the user has chosen. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  } catch {
    return 'America/Sao_Paulo';
  }
}
